import {useState} from "react";
import {CloseButton, Tabs} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {bffApi} from "../../chat/bffApi";
import {FunksjonalitetTilgangId} from "../../../types";
import {Dialog} from "./Dialog";
import {GenerateTemplatesTab} from "./GenerateTemplatesTab";
import {SendToDflowTab} from "./SendToDflowTab";
import {ModalType} from "./types";
import type {CollectionMeta, ExportDocument, TopicMeta} from "./types";
import classes from "./generateModal.module.css";
import modalClasses from "../checklist/modalStyles.module.css";

type Props = {
    opened: boolean;
    onClose: () => void;
    type: ModalType;
    topicMeta: TopicMeta;
    collectionMeta?: CollectionMeta;
    documents: ExportDocument[];
};

const TAB_TEMPLATE = "template";
const TAB_DFLOW = "dokumentflyt";

const tabsClassNames = {
    list: modalClasses.modalTabsList,
    tab: modalClasses.modalTab,
    panel: modalClasses.modalTabsPanel,
};

// Standalone docgen dialog — tabs "Mal" / "dflow" as in the legacy app.
// Both standalone buttons open it; `type` picks the start tab.
export const StandaloneDocumentModal = ({
    opened,
    onClose,
    type,
    topicMeta,
    collectionMeta,
    documents,
}: Props) => {
    const {t} = useTranslation();
    const {data: user} = bffApi.endpoints.getUser.useQuery();
    const [busy, setBusy] = useState(false);
    const hasDflow = !!user?.tilganger?.some((access) =>
        access.produkter?.some((p) => p.id === FunksjonalitetTilgangId.Dflow)
    );
    const startTab =
        hasDflow && type === ModalType.sendToDokumentflytStandalone
            ? TAB_DFLOW
            : TAB_TEMPLATE;

    return (
        <Dialog
            active={opened}
            onClose={onClose}
            // Esc is too easy to hit by accident. Closing mid-request
            // orphans an export or dflow send that cannot be aborted. The
            // backdrop already can't close it: `clickOutsideExits` defaults
            // to false.
            escapeExits={false}
            data-pw="generate-dialog"
        >
            <header className={classes.header}>
                <h2 className={classes.heading}>{t("Word.Generate")}</h2>
                <CloseButton
                    onClick={onClose}
                    disabled={busy}
                    aria-label={t("Action.Close", {defaultValue: "Close"})}
                />
            </header>
            <div className={classes.body}>
                <Tabs defaultValue={startTab} classNames={tabsClassNames}>
                    {/* Tabs lock while busy: the result belongs to the tab
                        that asked for it, and a second tab must not start a
                        request. */}
                    <Tabs.List>
                        <Tabs.Tab value={TAB_TEMPLATE} disabled={busy}>
                            {t("Word.Template")}
                        </Tabs.Tab>
                        {hasDflow && (
                            <Tabs.Tab value={TAB_DFLOW} disabled={busy}>
                                {t("name.Dflow")}
                            </Tabs.Tab>
                        )}
                    </Tabs.List>
                    <Tabs.Panel value={TAB_TEMPLATE}>
                        <GenerateTemplatesTab
                            isStandalone
                            onClose={onClose}
                            topicMeta={topicMeta}
                            collectionMeta={collectionMeta}
                            documents={documents}
                            busy={busy}
                            onBusyChange={setBusy}
                        />
                    </Tabs.Panel>
                    {hasDflow && (
                        <Tabs.Panel value={TAB_DFLOW}>
                            <SendToDflowTab
                                onClose={onClose}
                                topicMeta={topicMeta}
                                collectionMeta={collectionMeta}
                                documents={documents}
                                busy={busy}
                                onBusyChange={setBusy}
                            />
                        </Tabs.Panel>
                    )}
                </Tabs>
            </div>
        </Dialog>
    );
};
